<div class="flex flex-col gap-4 p-4">
  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-default text-xl font-bold">Procurement Dashboard</h1>
      <p class="text-secondary text-sm">Vendors, work orders and spend analysis</p>
    </div>
  </div>

  <!-- Filter Bar -->
  <div class="rounded-xl border bg-white px-4 py-1">
    <app-global-filter-panel [config]="filterConfig" (filtersChanged)="onFiltersChanged($event)" />
  </div>

  <!-- Contract KPIs -->
  <app-kpi-card-grid [cards]="workOrderKpis" title="WORK ORDER OVERVIEW" [columns]="5" />

  <!-- Expiring Contracts -->
  <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <app-work-order-expiry-tracker [dashboardType]="dashboardType" [filters]="filters" [requiredPermission]="'rfp_view'" />
  </div>

  <!-- Row 3: Work Order Pipeline + Spend Analysis -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <!-- Work Order Pipeline -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 class="text-secondary mb-3 text-sm font-semibold uppercase tracking-wide">Work Order Pipeline</h3>
      @if (!hasPipelinePermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingPipeline) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (pipelineData) {
        <!-- Status summary pills -->
        @if (pipelineData.by_status?.length > 0) {
          <div class="mb-4 flex flex-wrap gap-1.5">
            @for (s of pipelineData.by_status; track $index) {
              <span class="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold capitalize leading-none text-gray-700">
                <app-status-dot [color]="s.status | color" [title]="s.status"></app-status-dot>
                {{ s.status ?? 'Unknown' }} · {{ s.count }}
              </span>
            }
          </div>
        }
        <!-- Recent work orders -->
        @if (pipelineData.recent_jobs?.length > 0) {
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Recent Work Orders</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <div class="divide-y divide-gray-50">
            @for (job of pipelineData.recent_jobs; track job.id) {
              <a [routerLink]="['/procurement/work-orders/detail', job.id]" class="flex items-center gap-2.5 py-2 transition-colors hover:bg-gray-50">
                <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                  <mat-icon class="!text-sm text-blue-500">engineering</mat-icon>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="truncate text-xs font-semibold text-gray-800">{{ job.title }}</div>
                  <div class="text-[10px] text-gray-400">
                    @if (job.reference) {
                      <span class="font-medium text-gray-500">{{ job.reference }}</span> ·
                    }
                    {{ job.community_name ?? '—' }}
                    @if (job.vendor_name) {
                      · {{ job.vendor_name }}
                    }
                  </div>
                </div>
                <span class="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize leading-none text-gray-700">
                  <app-status-dot [color]="job.status | color" [title]="job.status"></app-status-dot>
                  {{ job.status }}
                </span>
              </a>
            }
          </div>
        } @else {
          <div class="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-8 text-center">
            <mat-icon class="!text-3xl text-gray-300">engineering</mat-icon>
            <span class="text-xs text-gray-400">No work orders in this period</span>
          </div>
        }
      }
    </div>

    <!-- Spend Analysis -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 class="text-secondary mb-3 text-sm font-semibold uppercase tracking-wide">Vendor Bill Spend Analysis (<span class="currency normal-case">&#xe001;</span>)</h3>
      @if (!hasSpendPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingSpend) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (spendData) {
        <div class="mb-4 grid grid-cols-2 gap-3">
          <div class="rounded-lg border border-orange-100 bg-orange-50 p-4 text-center">
            <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Payables</div>
            <div class="mt-1 text-2xl font-bold text-orange-700">{{ spendData.total_payables | number: '1.2-2' }}</div>
          </div>
          <div class="rounded-lg border border-green-100 bg-green-50 p-4 text-center">
            <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Paid</div>
            <div class="mt-1 text-2xl font-bold text-green-700">{{ spendData.total_paid | number: '1.2-2' }}</div>
          </div>
        </div>
        @if (spendData.by_vendor?.length > 0) {
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Top Vendors by Payables</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <!-- Header row -->
          <div class="mb-0.5 grid grid-cols-[1fr_90px_90px] px-2">
            <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Vendor</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Payable</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Paid</span>
          </div>
          <div class="divide-y divide-gray-50">
            @for (v of spendData.by_vendor; track $index) {
              <div class="grid grid-cols-[1fr_90px_90px] items-center gap-2.5 py-2 transition-colors hover:bg-gray-50">
                <div class="flex min-w-0 items-center gap-2.5">
                  <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                    <mat-icon class="!text-sm text-blue-500">store</mat-icon>
                  </div>
                  <span class="min-w-0 truncate text-xs font-medium text-gray-700">{{ v.name }}</span>
                </div>
                <span class="text-right text-xs font-semibold text-orange-700">{{ v.payable | number: '1.2-2' }}</span>
                <span class="text-right text-xs font-semibold text-green-700">{{ v.paid | number: '1.2-2' }}</span>
              </div>
            }
          </div>
        }
      }
    </div>
  </div>

  <!-- Bottom Row: Approvals + Timeline -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm lg:col-span-2">
      <app-approval-queue-list [filters]="filters" [requiredPermission]="'accounting_bills_approve'" />
    </div>
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-activity-timeline [requiredPermission]="'activity_logs_other'" />
    </div>
  </div>
</div>
